import { Observable, Subscription } from 'rxjs';
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, EventEmitter, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { RecaptchaComponent } from 'ng-recaptcha';

import { FormControlService } from '../../services/form-control.service';
import { PreChatService } from '../../services/pre-chat.service';
import { ChatParam } from '../../models/chat-param';
import { CHAT_TYPE } from '../../models/chat-type-model';
import { LogEventType } from '../../models/log-event-type.model';

@Component({
    selector: 'app-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss']
})
export class FormComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() settings;
    @Input() language;
    @Input() timezone;
    @Input() reCaptchaEnabled;
    @Input() reCaptchaSiteKey;
    @Output() chatFormSubmitted = new EventEmitter<void>();
    @ViewChild('captchaRef') captchaRef: RecaptchaComponent;
    @ViewChild('preChatForm') preChatForm: ElementRef;
    readonly reCaptchaFormControlName: string = 'reCaptcha';
    form: FormGroup;
    customParams$: Observable<ChatParam[]>;
    customTaskData$: Observable<ChatParam[]>;
    formSubmitted = false;
    documentReferrer = model.referrer;
    recaptchaResponse = '';
    overriddenSkillAddresses = model.overriddenSkillAddresses;
    targetSkillAddress = model.targetSkillAddress;
    activeForm: any;
    isBrowserSupported: boolean = model.isBrowserSupported;
    acceptInvitationSubscription: Subscription;
    reactiveFormSubmitSubscription: Subscription;
    @ViewChild('chatButtonWrapper') chatButtonWrapper: ElementRef;

    constructor(private preChatService: PreChatService, private formControlService: FormControlService) {
    }

    /*
    * Return true if proactive is enabled and has same form loaded as mentioned in _uwc('use-rule') command
    * Return true if proactive form is disabled
    * Return false if formTag is set to null for Reactive
    * Return false if formTag is set to null for Proactive
    * Return true for an incorrect formTag for Proactive
    */
    isActiveFormValid() {
        if (this.settings.formType === CHAT_TYPE.REACTIVE) {
            return this.settings.formTag !== null;
        }
        if (this.settings.formType === CHAT_TYPE.PROACTIVE) {
            if (this.settings.formTag === null)
                return false;
            if (this.settings.formTag !== this.settings.form.tag)
                return true;
            if (this.settings.formTag === this.settings.form.tag)
                return true;
        }
        return false;
    }

    ngOnInit() {
        console.info('[FormComponent] Initializing.');
        this.formSettings(this.settings.form);
        this.customParams$ = this.preChatService.bindToMessageEvents();
        this.customTaskData$ = this.preChatService.bindToCustomTaskData();
    }

    ngAfterViewInit() {
        if (this.captchaRef) {
            this.captchaRef.reset();
        }
        this.acceptInvitationSubscription = this.preChatService.invitationAccepted$.subscribe(invitationAccepted => {
            if (invitationAccepted) {
                const isValid = this.isActiveFormValid();
                console.info("[FormComponent] Received %cinvitationAccepted$%c. Active form valid:", 'color:magenta', '', isValid);
                if (isValid) {
                    this.logProactiveEvent(LogEventType.ChatFormOffer);
                    this.preChatService.showPreChatFormForProactive();
                } else {
                    this.submitForm();
                }
            }
        });
        this.reactiveFormSubmitSubscription = this.preChatService.reactiveFormSubmit$.subscribe(submit => {
            const isValid = this.isActiveFormValid();
            console.info("[FormComponent] Received %creactiveFormSubmit$%c. Active form valid:", 'color:magenta', '', isValid);
            if (submit && !isValid) {
                this.submitForm();
            }
        });
    }

    formSettings(activeForm) {
        this.activeForm = activeForm;
        if (!this.isActiveFormValid()) {
            this.form = this.formControlService.toFormGroup([]);
            return;
        }
        this.form = this.formControlService.toFormGroup(activeForm.rows);

        if (!this.isBrowserSupported) {
            this.form.disable();
        }

        if (this.reCaptchaEnabled) {
            this.formControlService.addFormControl(this.form, this.reCaptchaFormControlName, false);
        }
    }

    reCaptchaResponseCallback(captchaResponse: string) {
        this.recaptchaResponse = captchaResponse;
        this.submitForm();
    }

    submitForm() {
        console.info('[FormComponent] Submitting form.');
        if (this.settings.formType === CHAT_TYPE.PROACTIVE && this.settings.formTag === this.settings.form.tag) {
            //If the above mentioned conditions are true. then Proactive chat has a valid form
            // and submitForm() will be invoke only on clicking of chat form submit button, hence the log
            this.logProactiveEvent(LogEventType.ChatFormSubmit);
        }
        var prechatElement = <HTMLFormElement>this.preChatForm.nativeElement;
        prechatElement.submit();
        this.formSubmitted = true;
        this.chatFormSubmitted.emit();
        this.preChatService.setFormSubmitted();
    }

    onSubmit(event) {
        if (this.reCaptchaEnabled) {
            this.captchaRef.execute();
        } else {
            this.submitForm();
        }
    }

    logProactiveEvent(logEvent: LogEventType) {
        if (this.settings.formType !== CHAT_TYPE.PROACTIVE) {
            return;
        }
        this.preChatService.logEvent(logEvent, LogEventType[logEvent]);
    }

    ngOnDestroy() {
        if (this.acceptInvitationSubscription) {
            this.acceptInvitationSubscription.unsubscribe();
        }
        if (this.reactiveFormSubmitSubscription) {
            this.reactiveFormSubmitSubscription.unsubscribe();
        }
    }

    toggleBorder(hasFocus) {
        const borderClassName = 'chat-button-border';
        const wrapperNativeElement = this.chatButtonWrapper.nativeElement;
        wrapperNativeElement.classList.remove(borderClassName);

        if (hasFocus) {
            wrapperNativeElement.classList.add(borderClassName);
        }
    }
}
